// src/pages/ProjectsList.js
import React, { useEffect, useMemo, useState } from "react";
import { collection, onSnapshot, orderBy, query } from "firebase/firestore";
import { db } from "../firebaseConfig";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  FolderKanban,
  CheckCircle2,
  Activity,
  Gauge,
  PlayCircle,
} from "lucide-react";

/* ---------------- Stage options + mapping ---------------- */
const STAGES_UI = [
  "All",
  "MOU",
  "Site Survey",
  "Soil Testing",
  "Design & Structure",
  "Tender Process",
  "Work Started",
  "Excavation / Site Work",
  "Footing",
  "Plinth Beam",
  "Column & Beam Work",
  "Brick Masonry Work",
  "Plaster Work",
  "PEB Inspection",
  "PEB Installation",
  "Colour Work",
  "Logo Design & Branding",
  "Finishing",
  "Completed",
];

const STAGE_LABEL_TO_CODE = {
  All: "ALL",
  MOU: "MOU",
  "Site Survey": "SURVEY",
  "Soil Testing": "SOIL",
  "Design & Structure": "DESIGN",
  "Tender Process": "TENDER",
  "Work Started": "WORK",
  "Excavation / Site Work": "EXCAVATION",
  Footing: "FOOTING",
  "Plinth Beam": "PLINTH",
  "Column & Beam Work": "COLUMN_BEAM",
  "Brick Masonry Work": "BRICK",
  "Plaster Work": "PLASTER",
  "PEB Inspection": "PEB_INSPECTION",
  "PEB Installation": "PEB_INSTALL",
  "Colour Work": "COLOUR",
  "Logo Design & Branding": "LOGO",
  Finishing: "FINISH",
  Completed: "DONE",
};

const STAGE_ALIASES = {
  ALL: ["all"],
  MOU: ["mou"],
  SURVEY: ["survey", "site survey"],
  SOIL: ["soil", "soil testing"],
  DESIGN: ["design", "design & structure", "design and structure"],
  TENDER: ["tender", "tender process"],
  WORK: ["work", "work started"],
  EXCAVATION: ["excavation", "excavation / site work", "site work"],
  FOOTING: ["footing"],
  PLINTH: ["plinth", "plinth beam"],
  COLUMN_BEAM: ["column", "column & beam work", "column and beam work"],
  BRICK: ["brick", "brick masonry work"],
  PLASTER: ["plaster", "plaster work"],
  PEB_INSPECTION: ["peb inspection", "inspection"],
  PEB_INSTALL: ["peb install", "peb installation", "roof"],
  COLOUR: ["colour", "paint", "colour work", "painting"],
  LOGO: ["logo", "logo design & branding", "branding"],
  FINISH: ["finish", "finishing"],
  DONE: ["done", "completed", "completion", "complete"],
};

const stageToCode = (label) => STAGE_LABEL_TO_CODE[label] || label || "ALL";
const pct = (n) => Math.max(0, Math.min(100, Math.round(Number(n || 0))));

/* ---------------- utils ---------------- */
function normalizeProject(p, id) {
  let progress = Number(p.progressPercent || 0);
  if (!Number.isFinite(progress)) {
    const stages = p.stages || [];
    const t = stages.length || 0;
    const done = stages.filter(
      (s) => String(s.status || "").toLowerCase() === "completed"
    ).length;
    progress = t ? Math.round((done / t) * 100) : 0;
  }
  return { id, ...p, progressPercent: progress };
}

function matchStage(stageCode, proj) {
  if (!stageCode || stageCode === "ALL") return true;
  const skey = String(stageCode).toLowerCase();
  const aliasSet = new Set([skey]);
  for (const [code, list] of Object.entries(STAGE_ALIASES)) {
    if (code.toLowerCase() === skey || list.includes(skey)) {
      aliasSet.add(code.toLowerCase());
      list.forEach((t) => aliasSet.add(t));
    }
  }
  const curName = String(proj.currentStage || proj.status || "").toLowerCase();
  const curCode = String(proj.currentStageCode || "").toLowerCase();
  if (curName && [...aliasSet].some((a) => curName.includes(a))) return true;
  if (curCode && aliasSet.has(curCode)) return true;

  const stages = proj.stages || [];
  for (const s of stages) {
    const nm = String(s.name || "").toLowerCase();
    const cd = String(s.code || "").toLowerCase();
    const st = String(s.status || "").toLowerCase();
    if (nm && [...aliasSet].some((a) => nm.includes(a)) && st !== "pending")
      return true;
    if (cd && aliasSet.has(cd) && st !== "pending") return true;
  }
  return false;
}

/* ---------------- CSV helper ---------------- */
function downloadCSV(filename, rows) {
  if (!rows.length) {
    alert("No data to export");
    return;
  }
  const header = Object.keys(rows[0]).join(",");
  const body = rows
    .map((r) =>
      Object.values(r)
        .map((v) => `"${String(v ?? "").replace(/"/g, '""')}"`)
        .join(",")
    )
    .join("\n");
  const blob = new Blob([header + "\n" + body], {
    type: "text/csv;charset=utf-8;",
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

/* ---------------- small UI pieces ---------------- */

function KPI({ label, value, Icon, bgClass }) {
  return (
    <div
      className={
        "flex flex-col justify-between rounded-xl text-white shadow-sm px-4 py-3 " +
        bgClass
      }
    >
      <div className="flex items-center justify-between">
        <span className="text-[11px] uppercase tracking-wide opacity-90">
          {label}
        </span>
        {Icon && <Icon className="w-4 h-4 opacity-95" />}
      </div>
      <div className="mt-1 text-2xl font-bold leading-tight">{value}</div>
    </div>
  );
}

function MiniBarPanel({ title, data, accent = "blue" }) {
  const max = Math.max(1, ...data.map((d) => d.value || 0));
  const barClass =
    accent === "green"
      ? "bg-emerald-500"
      : accent === "orange"
      ? "bg-orange-500"
      : "bg-sky-500";

  return (
    <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4 flex flex-col">
      <div className="text-sm font-semibold text-slate-800 mb-1">{title}</div>
      <div className="text-[11px] text-slate-400 mb-3">
        Auto summary from current filtered projects
      </div>
      {data.length === 0 ? (
        <div className="flex-1 flex items-center justify-center text-[12px] text-slate-400">
          No data for current filters
        </div>
      ) : (
        <div className="space-y-2 flex-1">
          {data.map((d) => (
            <div key={d.label} className="text-[11px] text-slate-600">
              <div className="flex items-center justify-between mb-0.5">
                <span className="truncate max-w-[140px]">{d.label}</span>
                <span className="font-semibold text-slate-800">
                  {d.value}
                </span>
              </div>
              <div className="w-full h-2 bg-slate-100 rounded-full overflow-hidden">
                <div
                  className={barClass + " h-2 rounded-full"}
                  style={{
                    width:
                      d.value === 0 ? 0 : `${((d.value || 0) / max) * 100}%`,
                  }}
                />
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function StageSummaryPanel({ buckets }) {
  return (
    <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4">
      <div className="text-sm font-semibold text-slate-800 mb-1">
        Stage Summary
      </div>
      <div className="text-[11px] text-slate-400 mb-3">
        Buckets based on project progress %
      </div>
      <table className="w-full text-[11px] text-slate-600 border-collapse">
        <thead>
          <tr className="text-slate-500 bg-slate-50">
            <th className="text-left py-1 px-2 border border-slate-200">
              Bucket
            </th>
            <th className="text-right py-1 px-2 border border-slate-200 w-16">
              Count
            </th>
            <th className="text-right py-1 px-2 border border-slate-200 w-16">
              %
            </th>
          </tr>
        </thead>
        <tbody>
          {buckets.map((b) => (
            <tr key={b.label}>
              <td className="py-1 px-2 border border-slate-200">
                {b.label}
              </td>
              <td className="py-1 px-2 text-right border border-slate-200">
                {b.value}
              </td>
              <td className="py-1 px-2 text-right border border-slate-200">
                {b.total > 0
                  ? ((b.value / b.total) * 100).toFixed(1)
                  : "0.0"}
                %
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/* ---------------- Page ---------------- */

export default function ProjectsList() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const urlDistrict = searchParams.get("district") || "All";
  const urlStage = searchParams.get("stage") || "All";
  const urlQ = searchParams.get("q") || "";

  const [districts, setDistricts] = useState(["All"]);
  const [district, setDistrict] = useState(urlDistrict);
  const [stage, setStage] = useState(urlStage);
  const [searchTerm, setSearchTerm] = useState(urlQ);
  const [debouncedQ, setDebouncedQ] = useState(urlQ);

  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);

  const [page, setPage] = useState(1);
  const PAGE_SIZE = 24;

  /* ---------- Debounce search ---------- */
  useEffect(() => {
    const t = setTimeout(() => setDebouncedQ(searchTerm.trim()), 250);
    return () => clearTimeout(t);
  }, [searchTerm]);

  /* ---------- Firestore subscribe ---------- */
  useEffect(() => {
    setLoading(true);
    let unsubscribe;

    try {
      const qRef = query(
        collection(db, "projects"),
        orderBy("lastUpdated", "desc")
      );
      unsubscribe = onSnapshot(
        qRef,
        (snap) => {
          const arr = snap.docs.map((d) =>
            normalizeProject(d.data() || {}, d.id)
          );
          setProjects(arr);
          setLoading(false);
        },
        (err) => {
          console.warn("[projects orderBy fallback]", err?.message || err);
          const unsub2 = onSnapshot(
            collection(db, "projects"),
            (snap2) => {
              const arr2 = snap2.docs.map((d) =>
                normalizeProject(d.data() || {}, d.id)
              );
              setProjects(arr2);
              setLoading(false);
            },
            (e2) => {
              console.error("[projects plain subscribe error]", e2);
              setProjects([]);
              setLoading(false);
            }
          );
          unsubscribe = () => unsub2();
        }
      );
    } catch (e) {
      console.warn("[projects try-catch fallback]", e);
      const unsub2 = onSnapshot(
        collection(db, "projects"),
        (snap2) => {
          const arr2 = snap2.docs.map((d) =>
            normalizeProject(d.data() || {}, d.id)
          );
          setProjects(arr2);
          setLoading(false);
        },
        (e2) => {
          console.error("[projects plain subscribe error]", e2);
          setProjects([]);
          setLoading(false);
        }
      );
      unsubscribe = () => unsub2();
    }

    return () => typeof unsubscribe === "function" && unsubscribe();
  }, []);

  /* ---------- Derive districts ---------- */
  useEffect(() => {
    const uniq = Array.from(
      new Set(
        projects
          .map((p) => (p.district || "").trim())
          .filter(Boolean)
      )
    ).sort((a, b) => a.localeCompare(b));
    setDistricts(["All", ...uniq]);
    if (district !== "All" && !uniq.includes(district)) setDistrict("All");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projects]);

  /* ---------- URL sync ---------- */
  useEffect(() => {
    const params = new URLSearchParams();
    if (district && district !== "All") params.set("district", district);
    if (stage && stage !== "All") params.set("stage", stage);
    if (searchTerm?.trim()) params.set("q", searchTerm.trim());
    const next = params.toString();
    if (next !== searchParams.toString())
      setSearchParams(params, { replace: true });
  }, [district, stage, searchTerm, searchParams, setSearchParams]);

  /* ---------- Filters ---------- */
  const stageCode = useMemo(
    () => (stage === "All" ? "ALL" : stageToCode(stage)),
    [stage]
  );

  const filtered = useMemo(() => {
    const t = (debouncedQ || "").toLowerCase();

    return projects.filter((p) => {
      if (
        district !== "All" &&
        String(p.district || "").toLowerCase() !== district.toLowerCase()
      )
        return false;

      if (!matchStage(stageCode, p)) return false;

      if (t) {
        const hay = `${p.pacsName || ""} ${p.name || ""} ${
          p.projectId || p.id || ""
        } ${p.district || ""}`.toLowerCase();
        if (!hay.includes(t)) return false;
      }
      return true;
    });
  }, [projects, district, stageCode, debouncedQ]);

  useEffect(() => {
    setPage(1);
  }, [district, stage, debouncedQ]);

  /* ---------- Stats ---------- */
  const stats = useMemo(() => {
    const total = filtered.length;
    const completed = filtered.filter((p) => {
      const s = String(p.currentStage || "").toLowerCase();
      return s === "completed" || s === "done" || pct(p.progressPercent) >= 99;
    }).length;
    const notStarted = filtered.filter(
      (p) => pct(p.progressPercent) === 0
    ).length;
    const inProgress = filtered.filter(
      (p) => pct(p.progressPercent) > 0 && pct(p.progressPercent) < 75
    ).length;
    const nearDone = filtered.filter(
      (p) => pct(p.progressPercent) >= 75 && pct(p.progressPercent) < 99
    ).length;
    const avgProgress =
      total > 0
        ? Math.round(
            filtered.reduce(
              (sum, p) => sum + Number(p.progressPercent || 0),
              0
            ) / total
          )
        : 0;
    return { total, completed, notStarted, inProgress, nearDone, avgProgress };
  }, [filtered]);

  /* ---------- Analytics data ---------- */
  const districtChartData = useMemo(() => {
    const map = new Map();
    filtered.forEach((p) => {
      const key = p.district || "Unknown";
      map.set(key, (map.get(key) || 0) + 1);
    });
    return Array.from(map.entries())
      .map(([label, value]) => ({ label, value }))
      .sort((a, b) => b.value - a.value)
      .slice(0, 5);
  }, [filtered]);

  const progressChartData = useMemo(() => {
    const buckets = { "0–25%": 0, "25–50%": 0, "50–75%": 0, "75–100%": 0 };
    filtered.forEach((p) => {
      const v = pct(p.progressPercent);
      if (v < 25) buckets["0–25%"]++;
      else if (v < 50) buckets["25–50%"]++;
      else if (v < 75) buckets["50–75%"]++;
      else buckets["75–100%"]++;
    });
    return Object.entries(buckets).map(([label, value]) => ({ label, value }));
  }, [filtered]);

  const stageSummaryBuckets = useMemo(() => {
    const total = filtered.length;
    return progressChartData.map((b) => ({ ...b, total }));
  }, [progressChartData, filtered.length]);

  /* ---------- pagination slice ---------- */
  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const pageSlice = useMemo(() => {
    const start = (page - 1) * PAGE_SIZE;
    return filtered.slice(start, start + PAGE_SIZE);
  }, [filtered, page]);

  const navigateToDetail = (p) =>
    navigate(`/project/${p.id || p.projectId}`);

  /* ---------- CSV data for exports ---------- */
  const projectCSVRows = useMemo(
    () =>
      filtered.map((p, idx) => {
        const progress = pct(p.progressPercent);
        const updated =
          p.lastUpdated || p.updatedAt || p.createdAt || null;
        const dateStr = updated
          ? new Date(
              updated.toDate ? updated.toDate() : updated
            ).toLocaleDateString("en-IN")
          : "";
        return {
          Sr: idx + 1,
          PACS: p.pacsName || p.name || "",
          ProjectID: p.projectId || p.id || "",
          District: p.district || "",
          Stage: p.currentStage || p.stageName || "",
          Progress: `${progress}%`,
          LastUpdated: dateStr,
        };
      }),
    [filtered]
  );

  const districtCSVRows = useMemo(
    () => districtChartData.map((d) => ({ District: d.label, Projects: d.value })),
    [districtChartData]
  );

  const stageCSVRows = useMemo(
    () =>
      stageSummaryBuckets.map((b) => ({
        Bucket: b.label,
        Count: b.value,
        Percent:
          b.total > 0 ? ((b.value / b.total) * 100).toFixed(1) + "%" : "0.0%",
      })),
    [stageSummaryBuckets]
  );

  /* ====================== RENDER ====================== */

  return (
    <div className="min-h-screen bg-slate-100 px-3 py-6">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* HEADER */}
        <header>
          <h1 className="text-3xl md:text-4xl font-extrabold tracking-tight text-slate-900">
            Projects Dashboard
          </h1>
          <p className="mt-1 text-xs sm:text-sm text-slate-500 max-w-3xl">
            Central monitoring console for all PACS godown projects managed by
            Shine Agri Allied &amp; Marketing PMC.
          </p>
        </header>

        {/* KPI ROW */}
        <section className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3 text-xs sm:text-sm">
          <KPI
            label="Total Projects"
            value={stats.total}
            Icon={FolderKanban}
            bgClass="bg-slate-900"
          />
          <KPI
            label="Not Started"
            value={stats.notStarted}
            Icon={PlayCircle}
            bgClass="bg-pink-500"
          />
          <KPI
            label="In Progress"
            value={stats.inProgress}
            Icon={Activity}
            bgClass="bg-sky-600"
          />
          {/* yahi card pe pehle white bg issue tha – ab hamesha solid green */}
          <KPI
            label="Near Completion"
            value={stats.nearDone}
            Icon={Gauge}
            bgClass="bg-purple-600"
          />
          <KPI
            label="Completed"
            value={stats.completed}
            Icon={CheckCircle2}
            bgClass="bg-amber-600"
          />
        </section>

        {/* FILTER BLOCK */}
        <section className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm">
          <div className="grid grid-cols-1 md:grid-cols-[1.1fr_1.1fr_1.6fr] gap-4 items-end">
            <div className="text-sm">
              <label className="text-slate-600 mb-1 font-medium block">
                District
              </label>
              <select
                value={district}
                onChange={(e) => setDistrict(e.target.value)}
                className="w-full px-3 py-2 rounded-lg border border-slate-300 text-sm focus:outline-none focus:ring-2 focus:ring-sky-500"
              >
                {districts.map((d) => (
                  <option key={d} value={d}>
                    {d}
                  </option>
                ))}
              </select>
            </div>

            <div className="text-sm">
              <label className="text-slate-600 mb-1 font-medium block">
                Stage
              </label>
              <select
                value={stage}
                onChange={(e) => setStage(e.target.value)}
                className="w-full px-3 py-2 rounded-lg border border-slate-300 text-sm focus:outline-none focus:ring-2 focus:ring-sky-500"
              >
                {STAGES_UI.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </select>
            </div>

            <div className="text-sm">
              <label className="text-slate-600 mb-1 font-medium block">
                Search
              </label>
              <input
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search by PACS / project / ID / district…"
                className="w-full px-3 py-2 rounded-lg border border-slate-300 text-sm focus:outline-none focus:ring-2 focus:ring-sky-500"
              />
            </div>
          </div>
        </section>

        {/* ANALYTICS SECTION */}
        <section className="grid grid-cols-1 xl:grid-cols-[1.05fr_1.05fr_0.9fr] gap-4">
          <MiniBarPanel
            title="Projects by District"
            data={districtChartData}
            accent="blue"
          />
          <MiniBarPanel
            title="Progress Distribution"
            data={progressChartData}
            accent="orange"
          />
          <StageSummaryPanel buckets={stageSummaryBuckets} />
        </section>

        {/* PROJECT REGISTER TABLE + EXPORT BUTTONS */}
        <section className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm">
          <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
            <div>
              <h2 className="text-sm font-semibold text-slate-800">
                Project Register (Filtered View)
              </h2>
              <div className="text-[11px] text-slate-500">
                {filtered.length} record(s) found
              </div>
            </div>
            <div className="flex flex-wrap gap-2 text-[11px]">
              <button
                className="px-3 py-1.5 rounded-lg border border-slate-300 bg-white hover:bg-slate-50"
                onClick={() =>
                  downloadCSV("projects_register.csv", projectCSVRows)
                }
              >
                Export Projects CSV
              </button>
              <button
                className="px-3 py-1.5 rounded-lg border border-slate-300 bg-white hover:bg-slate-50"
                onClick={() =>
                  downloadCSV("district_summary.csv", districtCSVRows)
                }
              >
                District Summary CSV
              </button>
              <button
                className="px-3 py-1.5 rounded-lg border border-slate-300 bg-white hover:bg-slate-50"
                onClick={() =>
                  downloadCSV("stage_summary.csv", stageCSVRows)
                }
              >
                Stage Summary CSV
              </button>
            </div>
          </div>

          {loading ? (
            <div className="py-10 text-center text-sm text-slate-500">
              Loading projects…
            </div>
          ) : filtered.length === 0 ? (
            <div className="py-10 text-center text-sm text-slate-500">
              No projects found for this filter.
            </div>
          ) : (
            <>
              <div className="overflow-x-auto rounded-lg border border-slate-300">
                <table className="min-w-full text-[12px] text-slate-700 border-collapse">
                  <thead className="bg-slate-50">
                    <tr>
                      <th className="px-3 py-2 text-left border border-slate-300">
                        #
                      </th>
                      <th className="px-3 py-2 text-left border border-slate-300">
                        PACS / Project
                      </th>
                      <th className="px-3 py-2 text-left border border-slate-300">
                        District
                      </th>
                      <th className="px-3 py-2 text-left border border-slate-300 hidden md:table-cell">
                        Stage
                      </th>
                      <th className="px-3 py-2 text-center border border-slate-300">
                        Progress
                      </th>
                      <th className="px-3 py-2 text-left border border-slate-300 hidden md:table-cell">
                        Last Updated
                      </th>
                      <th className="px-3 py-2 text-right border border-slate-300">
                        Action
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {pageSlice.map((p, idx) => {
                      const progress = pct(p.progressPercent);
                      const rowIndex =
                        (page - 1) * PAGE_SIZE + (idx + 1);
                      const stage =
                        p.currentStage || p.stageName || "—";
                      const updated =
                        p.lastUpdated ||
                        p.updatedAt ||
                        p.createdAt ||
                        null;
                      const dateStr = updated
                        ? new Date(
                            updated.toDate ? updated.toDate() : updated
                          ).toLocaleDateString("en-IN", {
                            day: "2-digit",
                            month: "2-digit",
                            year: "numeric",
                          })
                        : "—";

                      return (
                        <tr
                          key={p.id}
                          className="hover:bg-slate-50 cursor-pointer"
                          onClick={() => navigateToDetail(p)}
                        >
                          <td className="px-3 py-2 border border-slate-300">
                            {rowIndex}
                          </td>
                          <td className="px-3 py-2 border border-slate-300">
                            <div className="font-semibold text-slate-900 truncate max-w-[220px]">
                              {p.pacsName || p.name || "—"}
                            </div>
                            <div className="text-[11px] text-slate-500">
                              ID: {p.projectId || p.id || "—"}
                            </div>
                          </td>
                          <td className="px-3 py-2 border border-slate-300">
                            {p.district || "—"}
                          </td>
                          <td className="px-3 py-2 border border-slate-300 hidden md:table-cell">
                            {stage}
                          </td>
                          <td className="px-3 py-2 border border-slate-300">
                            <div className="flex items-center gap-2 justify-center">
                              <div className="w-24 h-1.5 bg-slate-200 rounded-full overflow-hidden">
                                <div
                                  className="h-1.5 bg-gradient-to-r from-sky-500 to-emerald-500 rounded-full"
                                  style={{ width: `${progress}%` }}
                                />
                              </div>
                              <span className="text-[11px] text-slate-600">
                                {progress}%
                              </span>
                            </div>
                          </td>
                          <td className="px-3 py-2 border border-slate-300 hidden md:table-cell">
                            {dateStr}
                          </td>
                          <td className="px-3 py-2 border border-slate-300 text-right">
                            <button
                              className="text-[11px] text-sky-700 font-semibold hover:underline"
                              onClick={(e) => {
                                e.stopPropagation();
                                navigateToDetail(p);
                              }}
                            >
                              View »
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {totalPages > 1 && (
                <div className="mt-4 flex items-center justify-between text-[11px] text-slate-600">
                  <div>
                    Showing{" "}
                    <span className="font-semibold">
                      {(page - 1) * PAGE_SIZE + 1}
                    </span>{" "}
                    –{" "}
                    <span className="font-semibold">
                      {Math.min(page * PAGE_SIZE, filtered.length)}
                    </span>{" "}
                    of{" "}
                    <span className="font-semibold">
                      {filtered.length}
                    </span>{" "}
                    projects
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => setPage((p) => Math.max(1, p - 1))}
                      disabled={page === 1}
                      className={`px-3 py-1.5 rounded-lg border text-xs ${
                        page === 1
                          ? "border-slate-200 text-slate-300 bg-white"
                          : "border-slate-300 text-slate-700 bg-white hover:bg-slate-50"
                      }`}
                    >
                      Prev
                    </button>
                    <button
                      onClick={() =>
                        setPage((p) => Math.min(totalPages, p + 1))
                      }
                      disabled={page === totalPages}
                      className={`px-3 py-1.5 rounded-lg border text-xs ${
                        page === totalPages
                          ? "border-slate-200 text-slate-300 bg-white"
                          : "border-slate-300 text-slate-700 bg-white hover:bg-slate-50"
                      }`}
                    >
                      Next
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </section>
      </div>
    </div>
  );
}
